import type { ModelOption } from './types.js';

/** Estado de "órfão" de um módulo em `ai_module_configs`.
 *
 * Motivação: o `<select>` do `ModuleAssignments.tsx` filtra apenas modelos
 * `enabled=true` + provider `enabled=true` + `deprecated_at IS NULL` na lista
 * de options. Quando `default_model_id` aponta pra um modelo fora desse
 * conjunto (ex: o Superadmin desabilitou o Anthropic e os módulos ainda
 * apontam pros Claude), o browser faz fallback visual pra primeira option
 * disponível — mas o `value` interno do select permanece o UUID antigo, e
 * nada é persistido. O Alexandre caiu nesse bug em 2026-04-24 e ficou com
 * a UI mostrando "DeepSeek" enquanto o banco tinha Anthropic.
 *
 * Esta função é a fonte única de verdade do estado "órfão" — retorna um
 * discriminated union pra o consumer renderizar:
 *   - `ok`: tudo certo, sem mudança na UI
 *   - `disabled-model`: o modelo existe mas está indisponível (provider
 *     desabilitado, modelo desabilitado ou deprecated). Mostrar option
 *     selecionada com warning e forçar o user a trocar.
 *   - `missing-model`: o UUID não existe em `ai_models` — modelo foi deletado
 *     sem limpar a referência. Pior que disabled; mostrar ID truncado.
 *
 * Caller passa `allModels` incluindo modelos indisponíveis (com a flag
 * `isAvailable`) pra conseguir distinguir `disabled-model` de `missing-model`. */
export type OrphanState =
  | { kind: 'ok' }
  | { kind: 'disabled-model'; label: string; model: ModelOption }
  | { kind: 'missing-model'; label: string; modelId: string };

/** Resolve o estado órfão de um `default_model_id`.
 *
 * @param defaultModelId  UUID vindo de `ai_module_configs.default_model_id`.
 * @param allModels       Todos os modelos (disponíveis + indisponíveis),
 *                        cada um com a flag `isAvailable`.
 */
export function resolveOrphanState(
  defaultModelId: string | null,
  allModels: readonly ModelOption[],
): OrphanState {
  if (!defaultModelId) return { kind: 'ok' };

  const model = allModels.find((m) => m.id === defaultModelId);
  if (!model) {
    // UUID que não existe mais em ai_models — modelo foi deletado e a
    // referência ficou órfã. Truncamos o UUID pro user identificar em log.
    return {
      kind: 'missing-model',
      label: `⚠ Modelo removido (ID ${defaultModelId.slice(0, 8)}…)`,
      modelId: defaultModelId,
    };
  }

  if (!model.isAvailable) {
    // Modelo existe mas está indisponível. Label inclui o nome pro user
    // entender de onde veio (ex: lembrar que desabilitou o Anthropic).
    return {
      kind: 'disabled-model',
      label: `⚠ ${model.providerName} · ${model.modelId} (indisponível)`,
      model,
    };
  }

  return { kind: 'ok' };
}

/** Filtra `ModelOption[]` pra só os disponíveis (pro dropdown principal).
 * Extraído aqui pra manter a mesma fonte de verdade do `isAvailable`. */
export function filterAvailable(models: readonly ModelOption[]): ModelOption[] {
  return models.filter((m) => m.isAvailable);
}
